import { createContext, useContext, useMemo } from 'react';
import { useTheme, type DefaultTheme } from 'styled-components';
import type { MarketAccount } from '../../contexts/MarketAccountsContext';
import type { WLUser } from '../../providers/WLUsersContextProvider';
import type { FixingIndex } from '../../types';
import type { Aggregation } from '../../types/Aggregation';
import type { Counterparty } from '../../types/Counterparty';
import type { Currency } from '../../types/Currency';
import type { Customer } from '../../types/Customer';
import type { CustomerMarketAccount } from '../../types/CustomerMarketAccount';
import type { Market } from '../../types/Market';
import type { StrategyLike } from '../../types/OrderStrategy.types';
import type { Participant } from '../../types/Participant';
import type { Security } from '../../types/Security';
import type { SubAccount } from '../../types/SubAccount';
import type { OtherUser, User } from '../../types/User';
import type { WLOrgConfig } from '../../types/WLOrgConfig';
import type { IExposureDefinition, ISubaccount, MarketTypeEnum } from '../../types/types';
import type { ColDefIntl } from './columns/types';

/**
 * Configure blotter tables with settings
 */
export interface BlotterTableSettings {
  /** When exporting dates, if set to true, date value will not be formatted. */
  exportDatesRaw?: boolean;
  /** Set to true to stop the error banner from ever showing in the blotters */
  disableErrorBanner?: boolean;
  /** Set to true to show all decimals in formatted numbers within blotters */
  showAllDecimals?: boolean;
}

export interface BlotterTableContextProps {
  intl: ColDefIntl;
  marketDisplayNameByName?: Map<string, string>;
  marketTypeByName?: Map<string, MarketTypeEnum>;
  marketAccountsByName?: Map<string, MarketAccount>;
  marketAccountsByID?: Map<number, MarketAccount>;
  customers?: Customer[];
  counterpartiesByName?: Map<string, Counterparty>;
  customersByName?: Map<string, Customer>;
  strategiesByName?: Map<string, StrategyLike>;
  currenciesBySymbol?: Map<string, Currency>;
  securitiesBySymbol?: Map<string, Security>;
  loanParticipantsByName?: Map<string, Participant>;
  exposureDefinitionsByName?: Map<string, IExposureDefinition>;
  subAccountsByName?: Map<string, SubAccount>;
  subAccountsByID?: Map<number, SubAccount>;
  allSubAccounts?: ISubaccount[];
  theme?: DefaultTheme;
  userByEmail?: Map<string, OtherUser | WLUser>;
  userByID?: Map<string, OtherUser | WLUser>;
  currentUser?: User | WLUser;
  aggregationsByName?: Map<string, Aggregation>;
  fixingIndicesByName?: Map<string, FixingIndex>;
  fixingIndicesByID?: Map<string, FixingIndex>;

  /**
   * Configure blotter tables with settings
   */
  settings?: BlotterTableSettings;

  // These props below are actually marketplace only, but we need to move the column functions to marketplace first
  user?: unknown;
  marketsByName?: Map<string, Market>;

  // These props below are whitelabel only.
  customerMarketAccountsBySourceAccountID?: Map<string, CustomerMarketAccount>;
  wlOrgConfig?: WLOrgConfig;
}

export const BlotterTableContext = createContext<BlotterTableContextProps | undefined>(undefined);
BlotterTableContext.displayName = 'BlotterTableContext';

export function useBlotterTableContext(): BlotterTableContextProps {
  const theme = useTheme();
  const context = useContext(BlotterTableContext);
  if (!context) {
    throw new Error('useBlotterTableContext must be used within a BlotterTableContextProvider');
  }

  return useMemo(
    () =>
      ({
        ...context,
        theme,
      }) satisfies BlotterTableContextProps,
    [theme, context]
  );
}
